import * as React from "react";
import { FormattedMessage } from "react-intl";

import { prepareRequest } from "soapbox/actions/consumer-auth";
import { Button, Card, CardBody, Stack, Text } from "soapbox/components/ui";
import VerificationBadge from "soapbox/components/verification_badge";
import RegistrationForm from "soapbox/features/auth_login/components/registration_form";
import {
  useAppDispatch,
  useAppSelector,
  useFeatures,
  useSoapboxConfig,
} from "soapbox/hooks";
import { capitalize } from "soapbox/utils/strings";
import classNames from "classnames";
import { useTheme } from "soapbox/hooks";
import defaultLogo from "../../../images/mra-logo.png";

const LandingPage = () => {
  const dispatch = useAppDispatch();
  const features = useFeatures();
  const soapboxConfig = useSoapboxConfig();
  const pepeEnabled =
    soapboxConfig.getIn(["extensions", "pepe", "enabled"]) === true;

  const instance = useAppSelector((state) => state.instance);
  const pepeOpen = useAppSelector(
    (state) => state.verification.instance.get("registrations") === true,
  );

  const userCount = instance.stats.get("user_count");
  const darkMode = useTheme() === "dark";

  const homeDescription = React.useMemo(() => {
    const format = new Intl.NumberFormat().format(userCount);
    return soapboxConfig.homeDescription.replace("[users]", format);
  }, [userCount, soapboxConfig]);

  /** Registrations are closed */
  const renderClosed = () => {
    return (
      <Stack space={3} data-testid="registrations-closed">
        <Text size="xl" weight="bold" align="center">
          <FormattedMessage
            id="notification.user_approved"
            defaultMessage="Welcome to {instance}"
            values={{ instance: instance.title }}
          />
        </Text>
        <Text theme="muted" align="center">
          <FormattedMessage
            id="registration.closed_message_v2"
            defaultMessage="Created and run by MRA members, for MRA members.{br}{br}<b>Access is only available to current MRA members.</b>{br}{br}{clientsLink}{br}{betaLink}"
            values={{
              instance: instance.title,
              br: <br />,
              b: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              clientsLink: (
                <a
                  href="https://docs.akkoma.dev/stable/clients/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  Compatible clients (Windows/Mac/Linux/Android)
                </a>
              ),
              betaLink: (
                <a
                  href="https://testflight.apple.com/join/3Ik3KBjj"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 hover:underline"
                >
                  Potential client (iOS/Apple)
                </a>
              ),
            }}
          />
        </Text>
      </Stack>
    );
  };

  /** Custom Registrations */
  const renderCustom = () => {
    const { customRegProvider } = soapboxConfig;
    const { customRegUrl } = soapboxConfig;
    const onClickUrl = () => {
      window.open(customRegUrl);
    };

    return (
      <Stack space={3}>
        <Stack>
          <Text size="2xl" weight="bold" align="center">
            <FormattedMessage
              id="registrations.redirect"
              defaultMessage="No account yet?"
            />
          </Text>
        </Stack>

        <Button onClick={onClickUrl} theme="primary" block>
          <FormattedMessage
            id="registration.custom_provider_tooltip"
            defaultMessage="Sign up with {provider}"
            values={{ provider: capitalize(customRegProvider) }}
          />
        </Button>
      </Stack>
    );
  };

  /** Mastodon API registrations are open */
  const renderOpen = () => {
    return <RegistrationForm />;
  };

  /** Display login button for external provider. */
  const renderProvider = () => {
    const { authProvider } = soapboxConfig;

    return (
      <Stack space={3}>
        <Stack>
          <Text size="2xl" weight="bold" align="center">
            <FormattedMessage
              id="registrations.get_started"
              defaultMessage="Let's get started!"
            />
          </Text>
        </Stack>

        <Button
          onClick={() => dispatch(prepareRequest(authProvider))}
          theme="primary"
          block
        >
          <FormattedMessage
            id="oauth_consumer.tooltip"
            defaultMessage="Sign in with {provider}"
            values={{ provider: capitalize(authProvider) }}
          />
        </Button>
      </Stack>
    );
  };

  /** Pepe API registrations are open */
  const renderPepe = () => {
    return (
      <Stack space={3} data-testid="registrations-pepe">
        <VerificationBadge className="h-16 w-16 mx-auto" />

        <Stack>
          <Text size="2xl" weight="bold" align="center">
            <FormattedMessage
              id="registrations.get_started"
              defaultMessage="Let's get started!"
            />
          </Text>
          <Text theme="muted" align="center">
            <FormattedMessage
              id="registrations.tagline"
              defaultMessage="Social Media Without Discrimination"
            />
          </Text>
        </Stack>

        <Button to="/verify" theme="primary" block>
          <FormattedMessage
            id="registrations.create_account"
            defaultMessage="Create an account"
          />
        </Button>
      </Stack>
    );
  };

  // Render registration flow depending on features
  const renderBody = () => {
    if (soapboxConfig.authProvider) {
      return renderProvider();
    } else if (pepeEnabled && pepeOpen) {
      return renderPepe();
    } else if (features.accountCreation && instance.registrations) {
      return renderOpen();
    } else if (soapboxConfig.customRegProvider) {
      return renderCustom();
    } else {
      return renderClosed();
    }
  };

  return (
    <div
      className={classNames(
        "min-h-screen",
        darkMode ? "bg-[#0e1925]" : "bg-[#f0f0f0]",
      )}
    >
      <main className="mt-16 sm:mt-24" data-testid="homepage">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 py-12">
            <div className="px-4 sm:px-6 sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left lg:flex">
              <div className="w-full">
                <Stack space={5}>
                  <div className="mt-5 lg:mt-6">
                    <img
                      src={instance.logo || defaultLogo}
                      alt={instance.title}
                      className="h-80 w-auto"
                    />
                  </div>
                </Stack>
              </div>
            </div>
            <div className="sm:mt-24 lg:mt-0 lg:col-span-6 self-center">
              <Card
                size="xl"
                variant="rounded"
                className="sm:max-w-md sm:w-full sm:mx-auto"
              >
                <CardBody>{renderBody()}</CardBody>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
